/** Магазины (offline locations) — список + create/edit/delete. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MapPin, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";

interface Store {
  id: number;
  name: string;
  city: string;
  address: string;
  phone: string | null;
  schedule: string | null;
  lat: string | number | null;
  lng: string | number | null;
  sort_order: number;
  is_visible: boolean;
  is_warehouse: boolean;
  warehouse_code: string | null;
  priority: number;
}

type FormState = {
  name: string;
  city: string;
  address: string;
  phone: string;
  schedule: string;
  lat: string;
  lng: string;
  sort_order: number;
  is_visible: boolean;
  is_warehouse: boolean;
  warehouse_code: string;
  priority: number;
};

const EMPTY_FORM: FormState = {
  name: "",
  city: "",
  address: "",
  phone: "",
  schedule: "",
  lat: "",
  lng: "",
  sort_order: 0,
  is_visible: true,
  is_warehouse: true,
  warehouse_code: "",
  priority: 0,
};

function buildPayload(form: FormState): Record<string, unknown> {
  return {
    name: form.name.trim(),
    city: form.city.trim(),
    address: form.address.trim(),
    phone: form.phone.trim() || null,
    schedule: form.schedule.trim() || null,
    lat: form.lat.trim() ? Number(form.lat) : null,
    lng: form.lng.trim() ? Number(form.lng) : null,
    sort_order: form.sort_order,
    is_visible: form.is_visible,
    is_warehouse: form.is_warehouse,
    warehouse_code: form.warehouse_code.trim() || null,
    priority: form.priority,
  };
}

function storeToForm(s: Store): FormState {
  return {
    name: s.name,
    city: s.city,
    address: s.address,
    phone: s.phone ?? "",
    schedule: s.schedule ?? "",
    lat: s.lat != null ? String(s.lat) : "",
    lng: s.lng != null ? String(s.lng) : "",
    sort_order: s.sort_order,
    is_visible: s.is_visible,
    is_warehouse: s.is_warehouse,
    warehouse_code: s.warehouse_code ?? "",
    priority: s.priority,
  };
}

export function StoresListPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);

  const { data: stores, isLoading } = useQuery({
    queryKey: ["admin-stores"],
    queryFn: async () => {
      const { data } = await api.get("/stores");
      return data as Store[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/stores/${id}`);
    },
    onSuccess: () => {
      toast.success(t("common.toast.deleted"));
      queryClient.invalidateQueries({ queryKey: ["admin-stores"] });
    },
    onError: (e: any) =>
      toast.error(
        e?.response?.data?.detail ?? t("common.toast.errorOccurred"),
      ),
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить магазин?",
  });

  const toggleMutation = useMutation({
    mutationFn: async ({
      id,
      is_visible,
    }: {
      id: number;
      is_visible: boolean;
    }) => {
      await api.patch(`/stores/${id}`, { is_visible });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-stores"] }),
    onError: (e: any) =>
      toast.error(
        e?.response?.data?.detail ?? t("common.toast.errorOccurred"),
      ),
  });
  const togglingId = toggleMutation.isPending
    ? (toggleMutation.variables?.id ?? null)
    : null;

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading">Магазины</h1>
        <Button onClick={() => setEditingId("new")}>
          <Plus className="w-4 h-4 mr-1" />
          {t("common.create")}
        </Button>
      </div>

      <StoreDialog
        open={editingId !== null}
        onClose={() => setEditingId(null)}
        mode={editingId === "new" ? "create" : "edit"}
        initial={
          editingId === "new" || editingId === null
            ? EMPTY_FORM
            : storeToForm(stores?.find((s) => s.id === editingId) as Store)
        }
        storeId={typeof editingId === "number" ? editingId : undefined}
      />

      {stores?.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Магазинов нет. Нажмите «Создать» чтобы добавить первый.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {stores?.map((s) => (
          <Card key={s.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base truncate">{s.name}</CardTitle>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {s.city}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Switch
                    checked={s.is_visible}
                    disabled={togglingId === s.id}
                    onCheckedChange={(is_visible) =>
                      toggleMutation.mutate({ id: s.id, is_visible })
                    }
                  />
                  {togglingId === s.id ? (
                    <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {s.is_visible ? "Виден" : "Скрыт"}
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-start gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="break-words">{s.address}</span>
              </div>
              {s.phone && (
                <div className="text-muted-foreground">📞 {s.phone}</div>
              )}
              {s.schedule && (
                <div className="text-muted-foreground">🕒 {s.schedule}</div>
              )}
              {s.lat != null && s.lng != null && (
                <div className="text-xs text-muted-foreground">
                  GPS: {s.lat}, {s.lng}
                </div>
              )}

              <div className="flex items-center justify-between pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingId(s.id)}
                >
                  Редактировать
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => askDelete(s.id, s.name)}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {deleteDialog}
    </div>
  );
}

function StoreDialog({
  open,
  onClose,
  mode,
  initial,
  storeId,
}: {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  initial: FormState;
  storeId?: number;
}): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Новый магазин" : "Редактировать магазин"}
          </DialogTitle>
        </DialogHeader>
        {open && (
          <StoreForm
            key={storeId ?? "new"}
            initial={initial}
            onClose={onClose}
            mode={mode}
            storeId={storeId}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function StoreForm({
  initial,
  onClose,
  mode,
  storeId,
}: {
  initial: FormState;
  onClose: () => void;
  mode: "create" | "edit";
  storeId?: number;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(initial);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload(form);
      if (mode === "create") {
        await api.post("/stores", payload);
      } else if (storeId != null) {
        await api.patch(`/stores/${storeId}`, payload);
      }
    },
    onSuccess: () => {
      toast.success(
        mode === "create"
          ? "Магазин создан"
          : t("common.toast.saved"),
      );
      queryClient.invalidateQueries({ queryKey: ["admin-stores"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(
        e?.response?.data?.detail ?? t("common.toast.errorOccurred"),
      ),
  });

  const canSave =
    form.name.trim() && form.city.trim() && form.address.trim();

  return (
    <div>
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5 md:col-span-2">
            <Label>Название *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Best Digital — Самарканд"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Город *</Label>
            <Input
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="Самарканд"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Телефон</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+998 66 200 70 70"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Адрес *</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="ул. Почтовая, 36, Самарканд"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Режим работы</Label>
            <Input
              value={form.schedule}
              onChange={(e) => setForm({ ...form, schedule: e.target.value })}
              placeholder="Пн-Сб 9:00-20:00, Вс 10:00-18:00"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Широта (lat)</Label>
            <Input
              type="number"
              step="0.0000001"
              value={form.lat}
              onChange={(e) => setForm({ ...form, lat: e.target.value })}
              placeholder="41.311081"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Долгота (lng)</Label>
            <Input
              type="number"
              step="0.0000001"
              value={form.lng}
              onChange={(e) => setForm({ ...form, lng: e.target.value })}
              placeholder="69.240562"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Сортировка</Label>
            <Input
              type="number"
              value={form.sort_order}
              onChange={(e) =>
                setForm({
                  ...form,
                  sort_order: Number(e.target.value) || 0,
                })
              }
            />
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Switch
              checked={form.is_visible}
              onCheckedChange={(is_visible) =>
                setForm({ ...form, is_visible })
              }
            />
            <Label>Виден на сайте</Label>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Координаты можно скопировать из{" "}
          <a
            href="https://yandex.uz/maps/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            Яндекс.Карт
          </a>
          : правый клик на точке → «Что здесь?» → координаты.
        </p>

        {/* Warehouse-поля (1С-ready) */}
        <div className="border-t pt-3 space-y-3">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Параметры склада (1С)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="flex items-center gap-2 pt-6">
              <Switch
                checked={form.is_warehouse}
                onCheckedChange={(is_warehouse) =>
                  setForm({ ...form, is_warehouse })
                }
              />
              <Label>Это склад (есть остатки)</Label>
            </div>
            <div className="space-y-1.5">
              <Label>Код склада (1С)</Label>
              <Input
                value={form.warehouse_code}
                onChange={(e) =>
                  setForm({ ...form, warehouse_code: e.target.value })
                }
                placeholder="SKL_TASH_01"
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Приоритет fulfillment</Label>
              <Input
                type="number"
                value={form.priority}
                onChange={(e) =>
                  setForm({ ...form, priority: Number(e.target.value) || 0 })
                }
              />
              <p className="text-[10px] text-muted-foreground">
                Чем меньше — тем раньше отгружают
              </p>
            </div>
          </div>
        </div>

      </div>
      <DialogFooter className="pt-4">
        <Button variant="ghost" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={!canSave || saveMutation.isPending}
        >
          {mode === "create" ? t("common.create") : t("common.save")}
        </Button>
      </DialogFooter>
    </div>
  );
}
